import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    fetchJurisdictionOverrides,
    postJurisdictionOverride,
    postJurisdictionOverrideLift,
} from '@/lib/api'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import type {
    JurisdictionOverrideAddRequest,
    JurisdictionOverrideLiftRequest,
} from '@/types/jurisdictions'

const OVERRIDES_KEY = ['jurisdiction-overrides'] as const

export function useJurisdictionOverrides() {
    const { status } = useOperatorSession()
    return useQuery({
        queryKey: OVERRIDES_KEY,
        queryFn: ({ signal }) => fetchJurisdictionOverrides(signal),
        retry: false,
        enabled: status === 'ready',
    })
}

// No onError toast: the dialogs show the failure inline, next to the form that caused it.
export function useAddJurisdictionOverride() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: JurisdictionOverrideAddRequest) => postJurisdictionOverride(body),
        onSettled: () => qc.invalidateQueries({ queryKey: OVERRIDES_KEY }),
    })
}

export function useLiftJurisdictionOverride() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (vars: { id: string; body: JurisdictionOverrideLiftRequest }) =>
            postJurisdictionOverrideLift(vars.id, vars.body),
        onSettled: () => qc.invalidateQueries({ queryKey: OVERRIDES_KEY }),
    })
}
